/**
 * "Linked Processes" tab for an inventory record (equipment / component). Lists
 * the warehouse processes that touched this item via `useItemProcesses`, in a
 * read-only DataTable: code (link → process detail) · type · status · route ·
 * date. Reuses the shared process-format renderers so it stays 1-1 with the
 * Processes list. Loading / error / empty handled inline.
 */
import { ClipboardList } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import type { WarehouseProcess } from '@/api/capacities';
import { TableLink } from '@/components/table-link';
import { formatDate } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import {
  processRouteCell,
  processStatusCell,
  processTypeCell,
} from '../processes/process-format';

const muted = { color: 'var(--vn-fg-muted)' } as const;

const columns: ColumnDef<WarehouseProcess, unknown>[] = [
  {
    id: 'code',
    header: 'Code',
    cell: ({ row }) => (
      <TableLink to={`/capacities/processes/${row.original.id}`} title={row.original.code}>
        {row.original.code || row.original.id}
      </TableLink>
    ),
  },
  { id: 'type', header: 'Type', cell: ({ row }) => processTypeCell(row.original.type) },
  { id: 'status', header: 'Status', cell: ({ row }) => processStatusCell(row.original.status) },
  {
    id: 'route',
    header: 'Route',
    cell: ({ row }) => processRouteCell(row.original.source, row.original.destination),
  },
  {
    id: 'date',
    header: 'Date',
    cell: ({ row }) =>
      row.original.process_date ? (
        formatDate(row.original.process_date)
      ) : (
        <span style={muted}>—</span>
      ),
  },
];

export interface LinkedProcessesTabProps {
  processes: WarehouseProcess[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

/** DataTable of the processes linked to an inventory item. */
export function LinkedProcessesTab({
  processes,
  isLoading,
  isError,
  onRetry,
}: LinkedProcessesTabProps) {
  if (isError) return <TabError title="Failed to load linked processes" onRetry={onRetry} />;
  if (isLoading) return <TabLoading rows={4} />;

  return (
    <DataTable
      columns={columns}
      data={processes}
      getRowId={(r) => r.id}
      emptyState={
        <EmptyState
          icon={<ClipboardList size={24} />}
          title="No linked processes"
          description="No warehouse process has moved this item yet."
        />
      }
    />
  );
}
